import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, BehaviorSubject, throwError, timer, of } from 'rxjs';
import {
  retry,
  catchError,
  timeout,
  shareReplay,
  switchMap,
  filter,
  take,
  finalize,
  tap,
  map,
} from 'rxjs/operators';
import {
  AuditClientData,
  AuthTokenService,
} from '../services/auth-token.service';
import { environment } from 'src/environments/environment';
import { AuthRetryService } from '../services/auth-retry.service';
import { AuthToken } from '../model/auth-token.model';
import { DeviceSession } from 'src/app/core/models/device-session';

@Injectable({ providedIn: 'root' })
export class AuthTokenFacade {
  private authTokenService = inject(AuthTokenService);
  private authRetryService = inject(AuthRetryService);
  private platformId = inject(PLATFORM_ID);

  private readonly MAX_RETRIES = 2;
  private readonly TIMEOUT_MS = 10000; // 10 segundos
  private readonly RETRY_DELAY_MS = 2000; // 2 segundos entre reintentos

  private tokenFetchInProgress$ = new BehaviorSubject<boolean>(false);
  private lastFetchAttempt = 0;
  private readonly MIN_TIME_BETWEEN_ATTEMPTS = 5000; // 5 segundos mínimo entre intentos
  private pageStartTime = performance.timing
    ? performance.timing.navigationStart
    : Date.now();

  getToken(): string | null {
    return this.authTokenService.getToken();
  }

  getUuid(): string {
    return this.authTokenService.getUuid();
  }

  setToken(token: string) {
    this.authTokenService.setToken(token);
  }

  setUuid(uuid: string) {
    this.authTokenService.setUuid(uuid);
  }

  removeToken() {
    this.authTokenService.removeToken();
  }

  fetchToken(): Observable<AuthToken> {
    // Verificar si ya hay una solicitud en progreso
    if (this.tokenFetchInProgress$.value) {
      console.log('la obtencion del token esta en curso, waiting...');
      return this.tokenFetchInProgress$.pipe(
        filter((inProgress) => !inProgress),
        take(1),
        switchMap(() => {
          const token = this.getToken();
          if (token && this.isTokenValid(token)) {
            return new Observable<AuthToken>((subscriber) => {
              subscriber.next({ accessToken: token } as AuthToken);
              subscriber.complete();
            });
          }
          return this.performTokenFetch();
        })
      );
    }
    return this.performTokenFetch();
  }

  fetchUuid(): Observable<boolean> {
    return this.authTokenService.fetchUuid().pipe(
      map((resp) => {
        if (resp && resp.uuid) {
          this.setUuid(resp.uuid);
          return true;
        } else {
          console.error('Respuesta inválida al obtener UUID:', resp);
          return false;
        }
      }),
      catchError((error) => {
        console.error('Error al obtener UUID:', error);
        return [false];
      })
    );
  }

  private performTokenFetch(): Observable<AuthToken> {
    const now = Date.now();

    // Verificar estado de conectividad y política de reintentos
    if (!this.authRetryService.shouldAttemptRetry()) {
      const status = this.authRetryService.getStatus();
      console.log('No se puede intentar obtener el token:', status);
      return throwError(
        'La obtencion del token está bloqueada por la política de reintentos'
      );
    }

    // Verificar tiempo mínimo entre intentos
    if (now - this.lastFetchAttempt < this.MIN_TIME_BETWEEN_ATTEMPTS) {
      const remainingTime =
        this.MIN_TIME_BETWEEN_ATTEMPTS - (now - this.lastFetchAttempt);
      console.log(
        `Demasiado pronto para relizar otro intento. Waiting ${remainingTime}ms`
      );
      return throwError(
        'Rate limit: Demasiados intentos de obtención de token'
      );
    }

    this.lastFetchAttempt = now;
    this.tokenFetchInProgress$.next(true);

    const backoffDelay = this.authRetryService.getBackoffDelay();

    return this.getOrFetchUuid().pipe(
      switchMap((uuid) => this.performTokenFetchWithUuid(uuid, backoffDelay)),
      catchError((error) => {
        this.tokenFetchInProgress$.next(false);
        return throwError(error);
      })
    );
  }

  /**
   * Devuelve el UUID ya almacenado o, si no existe, lo obtiene del backend.
   * Usa el valor devuelto por la respuesta directamente (en vez de releer del
   * storage) para que funcione también en SSR, donde localStorage no existe y
   * setUuid() no persiste nada.
   */
  private getOrFetchUuid(): Observable<string> {
    const existing = this.getUuid() || '';
    if (existing) {
      return of(existing);
    }
    return this.authTokenService.fetchUuid().pipe(
      map((resp) => {
        if (resp && resp.uuid) {
          this.setUuid(resp.uuid);
          return resp.uuid;
        }
        throw new Error('Respuesta inválida al obtener UUID');
      }),
      catchError((error) => {
        console.error('Error al obtener UUID:', error);
        return throwError(error);
      })
    );
  }

  private performTokenFetchWithUuid(uuid: string, backoffDelay: number): Observable<AuthToken> {
    return this.authTokenService.fetchToken(uuid).pipe(
      timeout(this.TIMEOUT_MS),
      retry({
        count: this.MAX_RETRIES,
        delay: (error, retryCount) => {
          console.log(
            `Intento de obtener token ${retryCount} fallido, reintentando en ${backoffDelay}ms`,
            error
          );
          this.authRetryService.recordFailure();
          return timer(backoffDelay);
        },
      }),
      tap((response) => {
        // Registrar éxito
        console.log('Token obtenido exitosamente:', response);
        // Persistir el token en localStorage (auth0_token) para no volver a
        // solicitarlo en la siguiente carga.
        if (response && response.accessToken) {
          this.setToken(response.accessToken);
        }
        // auditorial crear nuevo usuario
        const data = this.getDataAudit(uuid);
        data.subscribe((auditData) => {
          this.fetchAudit(auditData);
        });
        this.authRetryService.recordSuccess();
      }),
      shareReplay(1),
      catchError((error) => {
        console.error('No se pudo obtener el token:', error);
        this.authRetryService.recordFailure();
        this.tokenFetchInProgress$.next(false);

        // Determinar el tipo de error y mensaje apropiado
        let errorMessage = 'No se pudo obtener el token de autenticación';

        if (error.name === 'TimeoutError') {
          errorMessage = 'Token request timed out';
        } else if (error.status >= 400 && error.status < 500) {
          errorMessage = 'Authentication service unavailable';
        } else if (error.status >= 500) {
          errorMessage = 'Server error while fetching token';
        } else if (!this.authRetryService.isOnline()) {
          errorMessage = 'No network connection available';
        }

        return throwError(errorMessage);
      }),
      // Finalizar el estado de "en progreso" cuando la solicitud termine
      finalize(() => {
        this.tokenFetchInProgress$.next(false);
      })
    );
  }

  public isTokenValid(token: string | null): boolean {
    if (!token) return false;
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    try {
      const payload = JSON.parse(atob(parts[1]));
      if (!payload || typeof payload.exp !== 'number') return false;
      const now = Math.floor(Date.now() / 1000);
      return payload.exp > now;
    } catch {
      return false;
    }
  }

  /**
   * Verifica si es seguro realizar una nueva solicitud de token
   */
  canFetchToken(): boolean {
    const now = Date.now();
    return (
      !this.tokenFetchInProgress$.value &&
      now - this.lastFetchAttempt >= this.MIN_TIME_BETWEEN_ATTEMPTS &&
      this.authRetryService.shouldAttemptRetry()
    );
  }

  /**
   * Resetea el estado del fetch en caso de emergencia
   * Usar solo en casos extremos donde el estado se haya corrompido
   */
  resetFetchState(): void {
    console.warn('Reseteando estado de obtención de token');
    this.tokenFetchInProgress$.next(false);
    this.lastFetchAttempt = 0;
    this.authRetryService.resetFailureCount();
  }

  /**
   * Obtiene el estado actual del fetch
   */
  isFetchInProgress(): boolean {
    return this.tokenFetchInProgress$.value;
  }

  /**
   * Obtiene información completa del estado de autenticación para debugging
   */
  getAuthStatus() {
    const token = this.getToken();
    return {
      hasToken: !!token,
      isTokenValid: this.isTokenValid(token),
      isFetchInProgress: this.isFetchInProgress(),
      canFetchToken: this.canFetchToken(),
      lastFetchAttempt: new Date(this.lastFetchAttempt).toISOString(),
      retryServiceStatus: this.authRetryService.getStatus(),
    };
  }

  /**
   * Fuerza una renovación del token (usar con precaución)
   */
  forceTokenRefresh(): Observable<AuthToken> {
    console.warn('Forzando renovación de token');
    this.resetFetchState();
    return this.fetchToken();
  }

  fetchAudit(data: AuditClientData): Observable<any> {
    return this.authTokenService.fetchAudit(data).pipe(
      tap((response) => {
        console.log('Audit registrado exitosamente:', response);
      }),
      catchError((error) => {
        console.error('No se pudo registrar el audit:', error);
        return throwError('No se pudo registrar el audit');
      })
    );
  }

  /**
   * Garantiza un token JWT válido para adjuntar a una solicitud autenticada.
   * Si ya existe un token válido (localStorage o memoria), lo devuelve sin red.
   * Si hay una obtención en curso, espera a que termine y reutiliza el resultado.
   * En caso de error o rate-limit devuelve null (la solicitud saldrá sin token).
   */
  ensureValidToken(): Observable<string | null> {
    const token = this.getToken();
    if (token && this.isTokenValid(token)) {
      return of(token);
    }
    if (this.tokenFetchInProgress$.value) {
      return this.tokenFetchInProgress$.pipe(
        filter((inProgress) => !inProgress),
        take(1),
        switchMap(() => {
          const t = this.getToken();
          return of(t && this.isTokenValid(t) ? t : null);
        })
      );
    }
    if (!this.canFetchToken()) {
      return of(null);
    }
    return this.fetchToken().pipe(
      map((resp: any) => {
        if (resp && resp.accessToken) {
          this.setToken(resp.accessToken);
          return resp.accessToken;
        }
        return null;
      }),
      catchError((error) => {
        console.error('No se pudo obtener el token para la solicitud autenticada:', error);
        return of(null);
      })
    );
  }

  getDataAudit(uuid: string): Observable<AuditClientData> {
    // SSR: detectar plataforma real. NO usar typeof window === 'undefined':
    // server.ts mockea window globalmente antes del render (lo necesitan otras
    // librerías), por lo que ese guard se invalida y el audit saldría del
    // servidor con campos vacíos → el backend responde 400 Bad Request.
    if (!isPlatformBrowser(this.platformId)) {
      return new Observable<AuditClientData>((subscriber) => {
        subscriber.complete();
      });
    }
    return new Observable<AuditClientData>((subscriber) => {
      // Calcular tiempo de respuesta desde inicio de navegación
      const now = Date.now();
      const requestDurationMs = now - this.pageStartTime;
      // Extraer dominio principal y subdominio
      const hostname = window.location.hostname; // ej: develop.agencies.galavail.com
      const parts = hostname.split('.');
      let domainName = hostname;
      let subdomainName = '';
      if (parts.length >= 2) {
        domainName = parts.slice(-2).join('.'); // galavail.com
        subdomainName = parts.slice(0, -2).join('.'); // develop.agencies
      }
      const accessPath =
        window.location.pathname +
        window.location.search +
        window.location.hash;
      const refererUrl =
        document.referrer && document.referrer !== ''
          ? document.referrer
          : window.location.href;
      // Obtener accessMethod del environment
      const accessMethod = environment.access_method || 'WEB-PRODUCCION';
      fetch('https://api.ipify.org?format=json')
        .then((response) => response.json())
        .then((data) => {
          subscriber.next({
            userUuid: uuid,
            domainName,
            subdomainName,
            accessPath,
            ipAddress: data.ip || '',
            userAgent: navigator.userAgent,
            sessionId: getStoredDeviceSession(),
            accessMethod,
            responseStatus: 200,
            refererUrl,
            requestDurationMs,
          });
          subscriber.complete();
        })
        .catch((error) => {
          console.error('No se pudo obtener la IP pública:', error);
          subscriber.next({
            userUuid: uuid,
            domainName,
            subdomainName,
            accessPath,
            ipAddress: '',
            userAgent: navigator.userAgent,
            sessionId: getStoredDeviceSession(),
            accessMethod,
            responseStatus: 200,
            refererUrl,
            requestDurationMs,
          });
          subscriber.complete();
        });
    });
  }
}

function getStoredDeviceSession(): string {
  if (typeof window === 'undefined') {
    return '';
  }
  const keys = Object.keys(localStorage);
  const deviceSessionKey = keys.find((key) =>
    key.startsWith('devicesessionLS')
  );
  console.log('deviceSessionKey', deviceSessionKey);
  if (deviceSessionKey) {
    const deviceSessionData: DeviceSession = JSON.parse(
      localStorage.getItem(deviceSessionKey) || 'null'
    );
    return deviceSessionData.idLS;
  }
  return '';
}
